import { DefaultError, useMutation } from "@tanstack/react-query";
import { CardBlockImage } from "entities/modules/types";
import { getImageKey } from "entities/modules/utils";
import { PAGE_THREE_CARDS_BLOCKS } from "modules/brands/pages/ModulesContainer/components/PageModules/components/ModuleDetailsDrawer/context/constants";
import { invalidateImageFilesQueries } from "shared/queries/useImageFileQueries";
import { MODULE_TOP_PRIVACY_TIPS_BLOCK } from "../../../modules/moduleTemplates/components/ModuleDetailsDrawer/context/constants";
import { updatePageModule } from "../api/updatePageModule";
import {
  getAllModulesByBrandQueryData,
  invalidateAllModulesByBrandQuery,
  setAllModulesByBrandQueryData,
} from "../queries/useAllModulesByBrandQuery";
import { invalidateModuleListByBrandPageQuery } from "../queries/useModuleListByBrandPageQuery";
import { invalidatePageModuleDetails } from "../queries/usePageModuleDetailsQuery";
import { invalidatePagesListByBrandsQuery } from "../queries/usePagesListByBrandQuery";
import { PageModuleDataToUpdate } from "../types";

type Params = {
  moduleId: string;
  moduleData: PageModuleDataToUpdate;
  brandId: string;
  pageId: string;
  cardBlockImages: CardBlockImage[];
  topPrivacyTipsBlockImage: CardBlockImage;
};

export const useUpdatePageModuleMutation = () => {
  return useMutation<void, DefaultError, Params>({
    mutationFn: async (params) => {
      await updatePageModule(params);
    },
    onSuccess: (
      _,
      {
        brandId,
        pageId,
        moduleData,
        cardBlockImages,
        moduleId,
        topPrivacyTipsBlockImage,
      },
    ) => {
      invalidatePageModuleDetails({ brandId, pageId, moduleId });
      invalidateModuleListByBrandPageQuery(brandId, pageId);

      const allModules = getAllModulesByBrandQueryData(brandId);

      if (allModules) {
        const updatedModules = allModules.map((module) =>
          module.id === moduleId ? { ...module, ...moduleData } : module,
        );

        setAllModulesByBrandQueryData(brandId, updatedModules);
        invalidateAllModulesByBrandQuery(brandId);
      }

      if (cardBlockImages.some((item) => item instanceof File)) {
        cardBlockImages.forEach((image, index) => {
          if (image) {
            const imageQueryKey = getImageKey(PAGE_THREE_CARDS_BLOCKS, index);
            invalidateImageFilesQueries([[imageQueryKey, moduleId]]);
          }
        });
      }

      if (topPrivacyTipsBlockImage) {
        const imageQueryKey = `${MODULE_TOP_PRIVACY_TIPS_BLOCK}-image`;
        invalidateImageFilesQueries([[imageQueryKey, moduleId]]);
      }

      // invalidate query in 3 seconds
      setTimeout(() => {
        invalidatePagesListByBrandsQuery(brandId);
      }, 3000);
    },
    onError: (e) => {
      console.error("Error occurred when creating module", e);
    },
  });
};
